import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicHeader from '@/components/layout/PublicHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { useDemoAnalytics, generateSessionId } from '@/hooks/useDemoAnalytics';
import { useBenchmarkCTA } from '@/hooks/useBenchmarkCTA';
import {
  LayoutDashboard, Users, FileText, CheckSquare, Search, Mic, UserSearch,
  Network, Bot, MessageCircle, Send, Sparkles, ArrowRight, Check,
  ChevronRight, Zap, Shield, Brain, TrendingUp, Clock, Target, Award,
  AlertTriangle, BarChart3, BookOpen, Layers, Activity, Play,
  ClipboardCheck, HelpCircle, Ticket, Download, Mail, Globe,
  GitBranch, Tag, Link2, Lightbulb, History, Bookmark
} from 'lucide-react';

const DemoPage = () => {
  const [sessionId] = useState(() => generateSessionId());
  const analytics = useDemoAnalytics(sessionId);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [chatMessage, setChatMessage] = useState('');
  const headerCTA = useBenchmarkCTA('demo_header');
  const footerCTA = useBenchmarkCTA('demo_footer');

  const handleTabChange = (tab) => { setActiveTab(tab); analytics.trackTabView(tab); };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="demo-page">
      <PublicHeader
        badge={<Badge className="bg-[#FF9500] text-white text-[10px]">INTERACTIVE DEMO</Badge>}
        extraButtons={
          <Link to={headerCTA.href} onClick={headerCTA.onClick}>
            <Button variant="outline" size="sm" className="text-xs border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500] hover:text-white" data-testid="demo-header-benchmark-btn" data-variant={headerCTA.variant}>
              <Target className="w-3.5 h-3.5 mr-1" />{headerCTA.label}
            </Button>
          </Link>
        }
      />

      {/* Banner */}
      <div className="bg-[#002FA7] text-white py-2.5">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs flex items-center justify-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Live demo with sample data. <Link to="/register" className="underline font-medium">Sign up</Link> to build your own ISO 30401-compliant knowledge base.</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-5">
          <TabsList className="bg-white border border-[#E5E7EB] flex-wrap h-auto gap-0.5 p-1">
            {[
              { value: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
              { value: 'compliance', icon: Shield, label: 'ISO Compliance' },
              { value: 'intelligence', icon: Brain, label: 'Predictive Intelligence' },
              { value: 'helpdesk', icon: Ticket, label: 'Help Desk & SLA' },
              { value: 'assessment', icon: ClipboardCheck, label: 'Capability Assessment' },
              { value: 'pkm', icon: Brain, label: 'Knowledge Engine (PKM)' },
              { value: 'workspace', icon: Users, label: 'KCS Workspace' },
              { value: 'assistant', icon: Bot, label: 'AI Assistant' },
            ].map(t => (
              <TabsTrigger key={t.value} value={t.value} className="text-xs" data-testid={`demo-tab-${t.value}`}>
                <t.icon className="w-3.5 h-3.5 mr-1" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ==================== DASHBOARD ==================== */}
          <TabsContent value="dashboard" className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A0A0A]">Dashboard Overview</h2>
              <p className="text-sm text-[#4B5563]">Real-time KPI metrics across your entire knowledge ecosystem</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {[
                { label: 'Knowledge Workers', value: '247', icon: Users, color: '#002FA7' },
                { label: 'Articles Published', value: '1,842', icon: FileText, color: '#34C759' },
                { label: 'Tasks Active', value: '88', icon: CheckSquare, color: '#FF9500' },
                { label: 'Knowledge Maps', value: '23', icon: Network, color: '#AF52DE' },
                { label: 'Webinars', value: '14', icon: Play, color: '#FF2D55' },
                { label: 'ISO Compliance', value: '84%', icon: Shield, color: '#002FA7' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <Card key={i} className="border-[#E5E7EB]"><CardContent className="p-3 text-center">
                    <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                    <p className="text-xl font-bold text-[#0A0A0A]">{s.value}</p>
                    <p className="text-[9px] text-[#9CA3AF]">{s.label}</p>
                  </CardContent></Card>
                );
              })}
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { icon: Brain, color: '#002FA7', title: 'Predictive Intelligence', desc: 'AI predicts knowledge gaps before they become crises. Scenario comparisons, key driver analysis, and auto-generated action plans.' },
                { icon: Shield, color: '#34C759', title: 'ISO 30401 Compliance', desc: 'Auto-evaluate 25 ISO clauses from real platform data. Live compliance score with evidence trails — no spreadsheets needed.' },
                { icon: Ticket, color: '#FF9500', title: 'Help Desk + KM Feedback Loop', desc: 'SLA-driven ticket system with auto-suggested articles, self-service tracking, knowledge gap detection, and Impact Reports.' },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <Card key={i} className="border-[#E5E7EB] hover:shadow-sm transition-shadow">
                    <CardContent className="p-5">
                      <Icon className="w-7 h-7 mb-3" style={{ color: f.color }} />
                      <h3 className="text-sm font-semibold mb-1">{f.title}</h3>
                      <p className="text-xs text-[#4B5563] leading-relaxed">{f.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              {[
                { icon: Mic, title: 'Voice Journaling', desc: 'AI transcription & summarization' },
                { icon: Activity, title: 'Degradation Tracking', desc: 'Flag stale knowledge automatically' },
                { icon: Layers, title: 'Knowledge Dependencies', desc: 'Map critical dependency nodes' },
                { icon: Target, title: 'Gap Predictions', desc: 'AI forecasts future knowledge gaps' },
                { icon: Download, title: 'PDF Export', desc: 'Download & share all tool data' },
                { icon: Mail, title: 'Email Sharing', desc: 'Distribute via email with branded templates' },
                { icon: BookOpen, title: 'Lessons Learned → Tasks', desc: 'One-click process improvement automation' },
                { icon: Award, title: 'Certification Verification', desc: 'Upload certs for HR review' },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <Card key={i} className="border-[#E5E7EB]"><CardContent className="p-3 flex items-start gap-2">
                    <Icon className="w-4 h-4 text-[#002FA7] mt-0.5 flex-shrink-0" />
                    <div><p className="text-xs font-semibold">{f.title}</p><p className="text-[10px] text-[#9CA3AF]">{f.desc}</p></div>
                  </CardContent></Card>
                );
              })}
            </div>
          </TabsContent>

          {/* ==================== ISO COMPLIANCE ==================== */}
          <TabsContent value="compliance" className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A0A0A] flex items-center gap-2"><Shield className="w-5 h-5 text-[#002FA7]" />ISO 30401:2018 Compliance Center</h2>
              <p className="text-sm text-[#4B5563]">Auto-evaluates all 25 clauses from real platform data</p>
            </div>
            <div className="flex items-center gap-4 mb-2">
              <Badge className="bg-[#34C759] text-white text-sm px-3 py-1">84% Compliant</Badge>
              <span className="text-xs text-[#9CA3AF]">21/25 clauses met from automated platform scanning</span>
            </div>
            <Progress value={84} className="h-3 mb-4" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[{ l: 'Compliant', v: 21, c: '#34C759' }, { l: 'Partial', v: 0, c: '#FF9500' }, { l: 'Gap', v: 4, c: '#FF3B30' }, { l: 'Not Started', v: 0, c: '#9CA3AF' }].map((s, i) => (
                <Card key={i} className="border-[#E5E7EB]"><CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold" style={{ color: s.c }}>{s.v}</p><p className="text-[9px] text-[#9CA3AF]">{s.l}</p>
                </CardContent></Card>
              ))}
            </div>
            <div className="space-y-1.5">
              {[
                { clause: '4.1', title: 'Organization context', status: 'compliant', evidence: 'Organization context documented' },
                { clause: '4.4.2', title: 'Knowledge development', status: 'compliant', evidence: '7 published articles, degradation tracking available' },
                { clause: '4.5', title: 'KM culture', status: 'compliant', evidence: '23 lessons learned, 10 assessment submissions' },
                { clause: '5.2', title: 'KM Policy', status: 'compliant', evidence: 'KM policy statement v1.0 approved' },
                { clause: '6.1', title: 'Risks and opportunities', status: 'gap', evidence: 'No risks registered — add via Risk Register' },
                { clause: '9.1', title: 'Monitoring and measurement', status: 'compliant', evidence: '3 report(s), 1 audit(s) — monitoring active' },
                { clause: '10.2', title: 'Continual improvement', status: 'compliant', evidence: '6 prediction(s), 3 report(s) — improvement data active' },
              ].map((item, i) => {
                const sc = item.status === 'compliant' ? '#34C759' : item.status === 'gap' ? '#FF3B30' : '#9CA3AF';
                return (
                  <Card key={i} className="border-[#E5E7EB]"><CardContent className="p-2.5 flex items-center gap-3">
                    <Badge className="text-[9px] px-1.5 font-mono" style={{ backgroundColor: '#002FA7', color: 'white' }}>{item.clause}</Badge>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium">{item.title}</span>
                      <p className="text-[9px] flex items-center gap-1" style={{ color: sc }}>
                        {item.status === 'compliant' ? <Check className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}{item.evidence}
                      </p>
                    </div>
                    <Badge className="text-[7px]" style={{ backgroundColor: sc + '15', color: sc }}>{item.status}</Badge>
                  </CardContent></Card>
                );
              })}
              <p className="text-[10px] text-[#9CA3AF] text-center pt-2">+ 18 more clauses — full 4.1–10.2 coverage with Context, Policy, Risk Register, Objectives, Document Control tabs</p>
            </div>
          </TabsContent>

          {/* ==================== PREDICTIVE INTELLIGENCE ==================== */}
          <TabsContent value="intelligence" className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A0A0A] flex items-center gap-2"><Brain className="w-5 h-5 text-[#AF52DE]" />Predictive Intelligence</h2>
              <p className="text-sm text-[#4B5563]">AI-powered predictions, scenario analysis, and prescriptive actions</p>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              {[
                { label: 'Prediction Accuracy', value: '87%', color: '#34C759', icon: Target },
                { label: 'Active Predictions', value: '12', color: '#AF52DE', icon: Sparkles },
                { label: 'Scenarios Compared', value: '8', color: '#007AFF', icon: BarChart3 },
                { label: 'Actions Generated', value: '24', color: '#FF9500', icon: Zap },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <Card key={i} className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.color + '12' }}>
                      <Icon className="w-5 h-5" style={{ color: s.color }} />
                    </div>
                    <div><p className="text-xl font-bold">{s.value}</p><p className="text-[10px] text-[#9CA3AF]">{s.label}</p></div>
                  </CardContent></Card>
                );
              })}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm">Key Predictions</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { text: 'Engineering team knowledge gaps expanding in cloud architecture', risk: 'high', time: '2-4 weeks' },
                    { text: 'Onboarding documentation staleness reaching critical levels', risk: 'medium', time: '1-3 months' },
                    { text: 'Cross-team collaboration declining in Q2 planning cycle', risk: 'high', time: '1-2 weeks' },
                  ].map((p, i) => {
                    const rc = p.risk === 'high' ? '#FF3B30' : '#FF9500';
                    return (
                      <div key={i} className="p-2.5 rounded-lg border-l-[3px] bg-[#FAFAFA]" style={{ borderLeftColor: rc }}>
                        <p className="text-xs text-[#0A0A0A]">{p.text}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge className="text-[7px]" style={{ backgroundColor: rc + '12', color: rc }}>{p.risk} risk</Badge>
                          <Badge variant="outline" className="text-[7px]"><Clock className="w-2.5 h-2.5 mr-0.5" />{p.time}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm">Prescriptive Actions (Auto-Generated)</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { action: 'Schedule cross-team knowledge sharing session for cloud architecture', priority: 'high', from: 'Gap prediction #1' },
                    { action: 'Assign documentation review sprint for onboarding materials', priority: 'medium', from: 'Degradation tracking' },
                    { action: 'Create Q2 KM objectives targeting collaboration metrics', priority: 'medium', from: 'Trend analysis' },
                  ].map((a, i) => (
                    <div key={i} className="p-2.5 rounded-lg bg-[#002FA7]/[0.03] border border-[#002FA7]/10">
                      <p className="text-xs text-[#0A0A0A]">{a.action}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge className="text-[7px] bg-[#002FA7]/10 text-[#002FA7]">{a.priority}</Badge>
                        <span className="text-[8px] text-[#9CA3AF]">From: {a.from}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== HELP DESK & SLA ==================== */}
          <TabsContent value="helpdesk" className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A0A0A] flex items-center gap-2"><Ticket className="w-5 h-5 text-[#FF9500]" />Help Desk & Service Desk</h2>
              <p className="text-sm text-[#4B5563]">SLA-driven ticketing integrated with Knowledge Management feedback loop</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { l: 'Total Tickets', v: '156', c: '#002FA7' }, { l: 'Self-Service Rate', v: '34%', c: '#34C759' },
                { l: 'Avg Resolution', v: '4.2h', c: '#007AFF' }, { l: 'SLA Compliance', v: '91%', c: '#34C759' }, { l: 'Escalated', v: '3', c: '#FF3B30' },
              ].map((s, i) => (
                <Card key={i} className="border-[#E5E7EB]"><CardContent className="p-3 text-center">
                  <p className="text-xl font-bold" style={{ color: s.c }}>{s.v}</p><p className="text-[9px] text-[#9CA3AF]">{s.l}</p>
                </CardContent></Card>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm">Recent Tickets</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { num: 'TKT-0042', title: 'Cannot access reports page', status: 'resolved', sla: 'met', by: 'Admin User' },
                    { num: 'TKT-0041', title: 'Need permissions for workspace', status: 'in_progress', sla: 'on_track', by: 'Sarah Chen' },
                    { num: 'TKT-0040', title: 'How to create a knowledge map?', status: 'closed', sla: 'met', by: 'Self-service' },
                  ].map((t, i) => {
                    const stc = { resolved: '#34C759', in_progress: '#AF52DE', closed: '#9CA3AF' }[t.status] || '#FF9500';
                    const slc = t.sla === 'met' ? '#34C759' : t.sla === 'on_track' ? '#34C759' : '#FF3B30';
                    return (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-[#E5E7EB]">
                        <span className="text-[9px] font-mono font-bold text-[#002FA7]">{t.num}</span>
                        <Badge className="text-[7px]" style={{ backgroundColor: stc + '12', color: stc }}>{t.status.replace('_', ' ')}</Badge>
                        <Badge className="text-[7px]" style={{ backgroundColor: slc + '12', color: slc }}>SLA {t.sla}</Badge>
                        <span className="text-xs text-[#0A0A0A] truncate flex-1">{t.title}</span>
                        <span className="text-[9px] text-[#9CA3AF]">{t.by}</span>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm">KM Feedback Loop</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { step: '1', title: 'Ticket Created', desc: 'AI suggests matching KB articles', icon: Ticket, color: '#002FA7' },
                      { step: '2', title: 'Self-Service or Agent', desc: '34% resolved via article suggestion', icon: Sparkles, color: '#34C759' },
                      { step: '3', title: 'Resolution → KB Draft', desc: 'One-click draft article from resolution', icon: FileText, color: '#AF52DE' },
                      { step: '4', title: 'Gap Detection', desc: 'Categories with low match rates flagged', icon: AlertTriangle, color: '#FF3B30' },
                      { step: '5', title: 'Impact Report', desc: 'Monthly SLA compliance + knowledge gaps', icon: BarChart3, color: '#FF9500' },
                    ].map((s, i) => {
                      const Icon = s.icon;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.color + '12' }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                          </div>
                          <div><p className="text-xs font-semibold">{s.title}</p><p className="text-[10px] text-[#9CA3AF]">{s.desc}</p></div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== CAPABILITY ASSESSMENT ==================== */}
          <TabsContent value="assessment" className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A0A0A] flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-[#002FA7]" />Capability Assessment</h2>
              <p className="text-sm text-[#4B5563]">KM maturity assessment with AI-powered recommendations, certification verification, and completion tracking</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border-[#E5E7EB]"><CardContent className="p-5 text-center">
                <div className="w-20 h-20 rounded-full bg-[#002FA7]/10 mx-auto mb-3 flex items-center justify-center">
                  <span className="text-2xl font-bold text-[#002FA7]">69%</span>
                </div>
                <Badge className="bg-[#007AFF] text-white mb-2">Intermediate Maturity</Badge>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div><p className="text-lg font-bold text-[#002FA7]">75%</p><p className="text-[8px] text-[#9CA3AF]">Process</p></div>
                  <div><p className="text-lg font-bold text-[#34C759]">65%</p><p className="text-[8px] text-[#9CA3AF]">People</p></div>
                  <div><p className="text-lg font-bold text-[#FF9500]">75%</p><p className="text-[8px] text-[#9CA3AF]">Technology</p></div>
                </div>
              </CardContent></Card>

              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Sparkles className="w-4 h-4 text-[#002FA7]" />AI Recommendations (sample)</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { text: 'Managers model knowledge-sharing behavior in weekly standups by starting with "Here\'s what I learned..."', type: 'people' },
                    { text: 'Assign rotating "knowledge seeker" role in team meetings to surface undocumented expertise', type: 'people' },
                    { text: 'Create lightweight templates for after-action reviews — max 5 fields, 10 min to complete', type: 'process' },
                  ].map((r, i) => {
                    const tc = r.type === 'people' ? '#002FA7' : r.type === 'process' ? '#FF9500' : '#34C759';
                    return (
                      <div key={i} className="p-2 rounded-lg border-l-[3px] bg-[#FAFAFA] border border-[#E5E7EB]" style={{ borderLeftColor: tc }}>
                        <p className="text-[10px] text-[#0A0A0A] leading-relaxed">{r.text}</p>
                        <Badge className="text-[7px] mt-1" style={{ backgroundColor: tc + '12', color: tc }}>{r.type}</Badge>
                      </div>
                    );
                  })}
                  <p className="text-[9px] text-[#9CA3AF]">7 KM areas × 3-5 recs each, tailored to maturity level</p>
                </CardContent>
              </Card>

              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm">Platform Features</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { label: 'Multi-select education/certifications', desc: 'CKMS, CPKM, CKME + custom "Other"' },
                    { label: 'Certification verification', desc: 'Upload certs → HR review → verified/rejected' },
                    { label: 'Email distribution', desc: 'Share assessments & results with workforce' },
                    { label: 'Completion tracker', desc: 'Deadline, reminders, overdue detection' },
                    { label: 'Team analytics', desc: 'Radar chart, maturity distribution, leaderboard' },
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-3 h-3 text-[#34C759] mt-0.5 flex-shrink-0" />
                      <div><p className="text-[10px] font-medium">{f.label}</p><p className="text-[9px] text-[#9CA3AF]">{f.desc}</p></div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== KNOWLEDGE ENGINE (PKM) ==================== */}
          <TabsContent value="pkm" className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A0A0A] flex items-center gap-2"><Brain className="w-5 h-5 text-[#AF52DE]" />Personal Knowledge Engine</h2>
              <p className="text-sm text-[#4B5563]">Turn personal notes into institutional memory — temporal notes, semantic ontology, decision log, and evolution maps</p>
            </div>

            {/* Top KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {[
                { label: 'PKM Notes', value: '1,248', icon: FileText, color: '#AF52DE' },
                { label: 'Ontology Classes', value: '47', icon: Network, color: '#002FA7' },
                { label: 'Decisions Logged', value: '312', icon: Lightbulb, color: '#FF9500' },
                { label: 'Voice Journals', value: '89', icon: Mic, color: '#FF2D55' },
                { label: 'Version History', value: '4,521', icon: History, color: '#34C759' },
                { label: 'Chat → PKM Pins', value: '67', icon: Bookmark, color: '#007AFF' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <Card key={i} className="border-[#E5E7EB]"><CardContent className="p-3 text-center">
                    <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                    <p className="text-xl font-bold text-[#0A0A0A]">{s.value}</p>
                    <p className="text-[9px] text-[#9CA3AF]">{s.label}</p>
                  </CardContent></Card>
                );
              })}
            </div>

            {/* Core pillars grid */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Temporal Notes */}
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-[#AF52DE]" />Temporal Notes & Evolution Map</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="p-2.5 rounded-lg border border-[#E5E7EB] bg-[#AF52DE]/5">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-[#0A0A0A]">"Q3 pricing strategy shift"</p>
                      <Badge className="bg-[#AF52DE]/10 text-[#AF52DE] text-[9px]">v4 · 3 days ago</Badge>
                    </div>
                    <p className="text-[10px] text-[#4B5563]">Evolution: <span className="text-[#FF3B30]">v1 bundled</span> → <span className="text-[#FF9500]">v2 tiered</span> → <span className="text-[#34C759]">v4 usage-based</span></p>
                    <div className="flex gap-1 mt-1.5">
                      <Badge variant="secondary" className="text-[8px]">pricing</Badge>
                      <Badge variant="secondary" className="text-[8px]">strategy</Badge>
                      <Badge variant="secondary" className="text-[8px]">Q3-2026</Badge>
                    </div>
                  </div>
                  <p className="text-[9px] text-[#9CA3AF]">Every edit is time-stamped — see how your thinking evolved over weeks, not just the latest snapshot.</p>
                </CardContent>
              </Card>

              {/* Decision Advisor */}
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[#FF9500]" />Decision Advisor & Log</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="p-2.5 rounded-lg border border-[#E5E7EB]">
                    <p className="text-xs font-medium mb-1">"Migrate auth to WorkOS SSO?"</p>
                    <div className="flex items-center gap-2 text-[10px] text-[#4B5563]">
                      <Badge className="bg-[#34C759]/10 text-[#34C759] text-[8px]">CHOSEN</Badge>
                      <span>4 options · 78% confidence</span>
                    </div>
                    <p className="text-[10px] text-[#6B7280] mt-1">Reasoning: enterprise SSO unlocks 3 pipeline deals, 14d to ship, amortized...</p>
                  </div>
                  <div className="p-2 rounded-lg bg-[#FF9500]/5 border border-[#FF9500]/20">
                    <p className="text-[10px] text-[#FF9500] font-medium flex items-center gap-1"><Zap className="w-3 h-3" />Ask the advisor → auto-extract options, risks, and recommendation as a loggable decision</p>
                  </div>
                </CardContent>
              </Card>

              {/* Semantic Ontology */}
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><GitBranch className="w-4 h-4 text-[#002FA7]" />Semantic Ontology</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-3 gap-1.5">
                    {['Methodology', 'KCS', 'Decision', 'Capability', 'Lesson', 'Expert'].map((c, i) => (
                      <Badge key={c} className="text-[9px] justify-center py-1" style={{ backgroundColor: ['#AF52DE','#002FA7','#FF9500','#34C759','#FF2D55','#007AFF'][i % 6] + '15', color: ['#AF52DE','#002FA7','#FF9500','#34C759','#FF2D55','#007AFF'][i % 6] }}>{c}</Badge>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#4B5563]">OWL-lite formal schema: classes, relations (domain→range), individuals, triples. Every note gets typed and linked.</p>
                  <div className="p-2 rounded-lg bg-[#002FA7]/5 border border-[#002FA7]/20">
                    <p className="text-[10px] text-[#002FA7] font-medium flex items-center gap-1"><Sparkles className="w-3 h-3" />AI-extract: feed 10 notes → ontology proposes classes + relations → you approve</p>
                  </div>
                </CardContent>
              </Card>

              {/* Voice Journal */}
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Mic className="w-4 h-4 text-[#FF2D55]" />Voice Journal → Note</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-[#FF2D55]/5 border border-[#FF2D55]/20">
                    <div className="w-7 h-7 rounded-full bg-[#FF2D55] flex items-center justify-center flex-shrink-0"><Mic className="w-3.5 h-3.5 text-white" /></div>
                    <div className="flex-1">
                      <p className="text-[10px] font-medium text-[#0A0A0A]">Recording · 2m 14s</p>
                      <p className="text-[9px] text-[#6B7280]">"Today's thinking on the MochaHost migration path..."</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <Badge className="bg-[#FF2D55]/10 text-[#FF2D55] text-[8px]">Whisper transcribed</Badge>
                    <Badge className="bg-[#34C759]/10 text-[#34C759] text-[8px]">AI-summarized</Badge>
                    <Badge className="bg-[#002FA7]/10 text-[#002FA7] text-[8px]">Tags auto-extracted</Badge>
                    <Badge className="bg-[#AF52DE]/10 text-[#AF52DE] text-[8px]">Linked to decision</Badge>
                  </div>
                  <p className="text-[9px] text-[#9CA3AF]">Walk-and-think → persisted as a searchable PKM note in seconds.</p>
                </CardContent>
              </Card>
            </div>

            {/* Chat → PKM loop highlight */}
            <Card className="border-[#E5E7EB] bg-gradient-to-br from-[#007AFF]/5 to-[#AF52DE]/5">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="w-4 h-4 text-[#007AFF]" />
                  <h3 className="text-sm font-semibold text-[#0A0A0A]">Chat ↔ PKM closed loop</h3>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-white border border-[#E5E7EB]">
                    <p className="text-[10px] font-semibold text-[#007AFF] mb-1">Pin chat → PKM</p>
                    <p className="text-[10px] text-[#4B5563]">One click promotes a chat discussion (with 5 messages of context) into a searchable note with auto-tags.</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white border border-[#E5E7EB]">
                    <p className="text-[10px] font-semibold text-[#AF52DE] mb-1">/attach in chat</p>
                    <p className="text-[10px] text-[#4B5563]">Type <code className="bg-[#F3F4F6] px-1 rounded">/attach</code> to inline a PKM note preview card inside a chat message — like Slack link unfurls.</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white border border-[#E5E7EB]">
                    <p className="text-[10px] font-semibold text-[#34C759] mb-1">Related discussions</p>
                    <p className="text-[10px] text-[#4B5563]">Every note shows which chat rooms reference it, so tribal knowledge becomes traceable.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI-powered actions */}
            <div className="grid md:grid-cols-4 gap-3">
              {[
                { icon: Search, color: '#002FA7', title: 'Semantic Search', desc: 'Ask natural language — AI translates to typed ontology queries' },
                { icon: FileText, color: '#AF52DE', title: 'Knowledge Briefs', desc: 'One-click analyst report with GPT-5.2 — PDF-ready for CKOs' },
                { icon: History, color: '#34C759', title: 'Ontology Diff', desc: 'Time-machine: what changed between any 2 dates, with AI commentary' },
                { icon: Tag, color: '#FF9500', title: 'Draft from Answer', desc: 'Turn any advisor answer into a logged decision in one click' },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <Card key={i} className="border-[#E5E7EB] hover:shadow-sm transition-shadow">
                    <CardContent className="p-4">
                      <Icon className="w-5 h-5 mb-2" style={{ color: f.color }} />
                      <h4 className="text-xs font-semibold mb-0.5 text-[#0A0A0A]">{f.title}</h4>
                      <p className="text-[10px] text-[#6B7280] leading-relaxed">{f.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>


          {/* ==================== KCS WORKSPACE ==================== */}
          <TabsContent value="workspace" className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A0A0A] flex items-center gap-2"><Users className="w-5 h-5 text-[#34C759]" />Collaborative KCS Workspace</h2>
              <p className="text-sm text-[#4B5563]">Knowledge-Centered Service methodology with daily standups, live capture, and AI pattern detection</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm">Workspace Rooms</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {['Engineering Knowledge Hub', 'Product Team Standup', 'Security Best Practices'].map((r, i) => (
                    <div key={i} className="p-2.5 rounded-lg border border-[#E5E7EB] hover:border-[#34C759] transition-colors cursor-pointer">
                      <p className="text-xs font-medium">{r}</p>
                      <p className="text-[9px] text-[#9CA3AF]">{5 + i * 3} captures • {2 + i} members</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm">Lessons Learned</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { title: 'Missing docs for API changes', impact: 'high', action: 'Add doc review to CI/CD' },
                    { title: 'Slow onboarding process', impact: 'medium', action: 'Create automated onboarding guide' },
                  ].map((l, i) => (
                    <div key={i} className="p-2.5 rounded-lg border border-[#E5E7EB]">
                      <p className="text-xs font-medium">{l.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className="text-[7px]" style={{ backgroundColor: l.impact === 'high' ? '#FF3B3012' : '#FF950012', color: l.impact === 'high' ? '#FF3B30' : '#FF9500' }}>{l.impact}</Badge>
                        <span className="text-[9px] text-[#007AFF]">Action: {l.action}</span>
                      </div>
                    </div>
                  ))}
                  <div className="p-2 rounded-lg bg-[#34C759]/5 border border-[#34C759]/20">
                    <p className="text-[10px] text-[#34C759] font-medium flex items-center gap-1"><Zap className="w-3 h-3" />Convert to Task — one-click process improvement</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB]"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Brain className="w-4 h-4 text-[#AF52DE]" />AI Pattern Detection</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="p-2.5 rounded-lg bg-[#AF52DE]/5 border border-[#AF52DE]/20">
                    <p className="text-[10px] font-semibold text-[#AF52DE]">Systemic Theme Detected</p>
                    <p className="text-[10px] text-[#4B5563] mt-1">"Documentation gaps at release boundaries" — appears in 4 lessons across 2 teams</p>
                    <div className="flex gap-2 mt-2">
                      <Badge className="text-[7px] bg-[#FF3B30]/10 text-[#FF3B30]">high priority</Badge>
                      <Badge className="text-[7px] bg-[#007AFF]/10 text-[#007AFF]">medium effort</Badge>
                    </div>
                  </div>
                  <p className="text-[9px] text-[#9CA3AF]">AI analyzes all lessons to detect recurring patterns and recommend systemic fixes</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== AI ASSISTANT ==================== */}
          <TabsContent value="assistant" className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A0A0A] flex items-center gap-2"><Bot className="w-5 h-5 text-[#002FA7]" />AI Knowledge Assistant</h2>
              <p className="text-sm text-[#4B5563]">Ask questions in natural language — searches articles, experts, tasks, lessons, and knowledge maps</p>
            </div>
            <Card className="border-[#E5E7EB] max-w-2xl">
              <CardContent className="p-5">
                <div className="space-y-3 mb-5">
                  <div className="flex gap-2 justify-end">
                    <div className="max-w-[75%] px-3 py-2 rounded-xl bg-[#002FA7] text-white text-xs">Who is an expert in cloud architecture?</div>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-lg bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0"><Bot className="w-3 h-3 text-[#002FA7]" /></div>
                    <div className="max-w-[75%]">
                      <div className="px-3 py-2 rounded-xl bg-[#F3F4F6] text-xs text-[#0A0A0A]">
                        Based on our expertise database, <strong>Michael Torres</strong> (Senior Architect, Engineering) is your go-to expert for Cloud Architecture. He has expertise in Cloud, Microservices, and DevOps. I also found 3 related articles and 1 knowledge map on cloud migration patterns.
                      </div>
                      <div className="flex gap-1 mt-1.5">
                        <Badge variant="secondary" className="text-[9px]"><UserSearch className="w-2.5 h-2.5 mr-0.5" />Michael Torres</Badge>
                        <Badge variant="secondary" className="text-[9px]"><FileText className="w-2.5 h-2.5 mr-0.5" />3 articles</Badge>
                        <Badge variant="secondary" className="text-[9px]"><Network className="w-2.5 h-2.5 mr-0.5" />1 map</Badge>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input placeholder="Try: 'What are the best practices for remote teams?'" value={chatMessage} onChange={e => setChatMessage(e.target.value)} className="text-xs" />
                  <Button size="sm" className="bg-[#002FA7]"><Send className="w-3.5 h-3.5" /></Button>
                </div>
                <p className="text-[9px] text-[#9CA3AF] mt-2 text-center">Powered by GPT-5.2 — Searches 10+ collections with intent parsing, relevance scoring, and spell correction</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <div className="mt-10 bg-[#0A0A0A] rounded-xl p-8 text-center text-white">
          <h2 className="text-2xl font-semibold mb-2">Ready to Transform Your Knowledge Management?</h2>
          <p className="text-[#9CA3AF] text-sm mb-5 max-w-lg mx-auto">Join organizations using KStreak to build a people-first, ISO 30401-compliant knowledge culture.</p>
          <div className="flex justify-center gap-3">
            <Link to="/register"><Button size="lg" className="bg-[#002FA7] hover:bg-[#001F7A]">Start Free Trial<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/why-kstreak"><Button size="lg" variant="outline" className="border-[#333] text-white hover:bg-[#1A1A1A]">Why KStreak?</Button></Link>
            <Link to={footerCTA.href} onClick={footerCTA.onClick}><Button size="lg" variant="outline" className="border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500] hover:text-white" data-testid="demo-footer-benchmark-btn" data-variant={footerCTA.variant}><Target className="w-4 h-4 mr-2" />{footerCTA.label}</Button></Link>
          </div>
          <div className="flex justify-center gap-6 mt-5 text-xs text-[#666]">
            <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" /> No credit card</span>
            <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" /> 14-day trial</span>
            <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Full access</span>
            <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> ISO 30401</span>
          </div>
        </div>
      </div>

      <footer className="bg-[#0A0A0A] text-white py-6 mt-10">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><img src="/logo.png" alt="KStreak" className="w-6 h-6 rounded object-contain" /><span className="text-sm font-medium">KStreak</span></div>
          <p className="text-xs text-[#9CA3AF]">ISO 30401:2018 Compliant Knowledge Management System</p>
        </div>
      </footer>
    </div>
  );
};

export default DemoPage;
